import { PRODUCTION_STAGES, type ProductionStage } from "../types";
import { useT } from "../i18n";

/**
 * Where the work actually is.
 *
 * "In progress" is true for three weeks and tells a client nothing. This shows the
 * same fact with a position in it, so "we're editing" reads as progress and
 * "still briefing" reads as a question worth asking.
 */
/** Keys, not sentences — see RequestTimeline for why a module constant cannot
 *  hold translated text. */
const LABELS: Record<ProductionStage, string> = {
  briefing: "stage.briefing",
  scripting: "stage.scripting",
  production: "stage.production",
  editing: "stage.editing",
  review: "stage.review",
  delivered: "stage.delivered",
};

export default function StageTracker({ stage }: { stage: ProductionStage | null }) {
  const t = useT();
  // No stage set is not stage one — claiming briefing has started when nobody said
  // so is exactly the fake-progress this screen exists to remove.
  if (!stage) return null;
  const current = PRODUCTION_STAGES.indexOf(stage);

  return (
    <div className="stage-tracker">
      <ol aria-label={t("stage.aria", { stage: t(LABELS[stage]) })}>
        {PRODUCTION_STAGES.map((step, index) => {
          const state = index < current ? "done" : index === current ? "current" : "todo";
          return (
            <li key={step} className={`stage-step is-${state}`} aria-current={state === "current" ? "step" : undefined}>
              <span className="stage-dot" aria-hidden="true" />
              <span className="stage-label">{t(LABELS[step])}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** The compact form, for a table cell. */
export function StagePill({ stage }: { stage: ProductionStage | null }) {
  // Its own hook: a component that renders text needs a language, and taking one
  // as a prop would make every caller thread it through.
  const t = useT();
  if (!stage) return null;
  return <span className={`stage-pill stage-${stage}`}>{t(LABELS[stage])}</span>;
}
